import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FavoritesQueryKeys } from '@enums/react-query-keys';
import { ApiResponse, ApiResponseError } from '@interfaces/api';
import { getUsersFavoritesProductsWithFilters } from '@api/favorites';
import { FAV_CONSTANTS } from '@constants/favorites';
import { HotFiltersQueryHookParams, HotFiltersResponse } from '@interfaces/api/responses/hot-filters.response';
import { AxiosError } from 'axios';
import logger from '@helpers/utils/logger/client';

const useFavoritesHotFiltersProductsQuery = ({
  isAuthenticated,
  userId,
  sortBy,
  hotFiltersQuery = {},
  paginationQuery = { offset: FAV_CONSTANTS.FAV_DEFAULT_OFFSET, limit: FAV_CONSTANTS.FAV_DEFAULT_LIMIT },
}: HotFiltersQueryHookParams) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError, error } = useInfiniteQuery<
    ApiResponse<HotFiltersResponse>
  >({
    queryKey: [FavoritesQueryKeys.FAVORITES_PRODUCTS, userId, sortBy, hotFiltersQuery, paginationQuery],
    queryFn: () =>
      getUsersFavoritesProductsWithFilters({
        userId,
        sortBy,
        hotFiltersQuery,
        paginationQuery,
      }),
    getNextPageParam: (lastPage) => {
      const productsLength = lastPage.meta.offset + FAV_CONSTANTS.FAV_DEFAULT_LIMIT;
      return productsLength < lastPage.meta.count ? productsLength : null;
    },
    staleTime: 1 * 60 * 1000,
    enabled: isAuthenticated,
    retry: false,
    onError: (err: AxiosError<{ errors: ApiResponseError }>) => {
      const errResponse = err as AxiosError<{ errors: ApiResponseError }>;
      const hasErrors = errResponse?.response?.data?.errors;

      if (hasErrors) {
        // Handle specific error messages
        logger.error('Error details:', errResponse.response.data.errors.detail);
      } else {
        // Handle unspecified errors
        logger.error('An unspecified error occurred.');
      }
    },
  });

  const favoriteProducts = useMemo(() => {
    return isFetching ? null : data?.pages?.flatMap((page) => page?.data?.items) || [];
  }, [data?.pages, isFetching]);

  const facets = useMemo(() => {
    return isFetching ? null : data?.pages?.flatMap((page) => page?.data?.facets?.fields) || {};
  }, [data?.pages, isFetching]);

  const totalCount = useMemo(() => {
    return isFetching ? null : data?.pages?.[0]?.meta?.count || 0;
  }, [data?.pages, isFetching]);

  return {
    data: favoriteProducts,
    facets,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isLoadingMoreProducts: isFetchingNextPage,
    isLoadingProducts: isFetching,
    meta: data?.pages?.[0]?.meta,
    isError,
    error: error as AxiosError<{ errors: ApiResponseError }>,
  };
};

export default useFavoritesHotFiltersProductsQuery;
